import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  fetchNamed,
  fetchTerrainKinds,
  fetchTerrainProfile,
  fetchTerrains,
  surveyTerrain,
  type NamedView,
  type SurveyFeatures,
  type TerrainProfileView,
  type TerrainSummary,
  type TerrainView,
} from '../api/catalog';
import { fetchHeightmap, fetchRecipesFor, type RecipeView } from '../api/missions';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { MissionMap } from './MissionMap';
import { Shell } from './Shell';
import { TerrainSheet } from './TerrainSheet';
import type { Heightmap } from './TerrainRelief';
import type { Selection } from './designModel';
import { VoiceRegionField } from './VoiceRegions';

type Profile = TerrainProfileView & {
  sites?: number;
  features?: SurveyFeatures | null;
  summary?: TerrainSummary | null;
};

/** The layers whose list the survey keeps short: their length is a cap, their total is the summary's. */
const SURVEY_LISTS = ['bridges', 'highGround', 'flatAreas', 'beaches', 'lakes'] as const;

const num = (n: number) => n.toLocaleString('ca');

/** What the map can show, each a layer somebody switches on; the order is the order of the chips. */
const LAYERS = [
  'settlements',
  'places',
  'airfields',
  'sites',
  'bridges',
  'highGround',
  'flatAreas',
  'beaches',
  'lakes',
] as const;
type Layer = (typeof LAYERS)[number];

/** A layer's pin: one of the map's own symbols and one of the game's own colours. */
const LOOK: Record<Layer, { symbol: string; colour: string }> = {
  settlements: { symbol: 'mil_dot', colour: 'ColorOrange' },
  places: { symbol: 'mil_dot', colour: 'ColorKhaki' },
  airfields: { symbol: 'mil_pickup', colour: 'ColorWEST' },
  sites: { symbol: 'mil_flag', colour: 'ColorRed' },
  bridges: { symbol: 'mil_box', colour: 'ColorBrown' },
  highGround: { symbol: 'mil_triangle', colour: 'ColorGreen' },
  flatAreas: { symbol: 'mil_dot', colour: 'ColorYellow' },
  beaches: { symbol: 'mil_dot', colour: 'ColorPink' },
  lakes: { symbol: 'mil_dot', colour: 'ColorBlue' },
};

/** More pins than this in one layer and the map is a rash, not a map; the count still says how many there are. */
const MAX_PINS = 400;

const grid = (x: number, y: number) =>
  `${String(Math.round(x / 100)).padStart(3, '0')} ${String(Math.round(y / 100)).padStart(3, '0')}`;

/**
 * One terrain of the catalogue (2026-09-19): the map the wizard draws, with
 * what the scan named on it and what the survey found on its ground, a layer
 * at a time, and the same facts as tables under it. It reads what is already
 * stored and asks the game for nothing.
 *
 * Every figure is the last completed survey's, read again each time the page
 * opens (the profile is never cached), so a re-survey shows its data here. A
 * figure the survey did not measure is left out: the lists the survey keeps
 * short (the 400 highest points, flattest spots, beaches) say how many the
 * terrain has only when the survey counted them (the owner, 2026-10-12: the
 * header read «400 · 400 · 400», which was the cap).
 */
export function TerrainDetail() {
  const { world = '' } = useParams();
  const [params] = useSearchParams();
  const hash = params.get('set');
  const t = ca.terrainPage;
  const { me } = useAuth();
  const [terrain, setTerrain] = useState<TerrainView | null>(null);
  const [profile, setProfile] = useState<Profile | null | undefined>(undefined);
  const [named, setNamed] = useState<NamedView | null>(null);
  const [relief, setRelief] = useState<Heightmap | null>(null);
  const [on, setOn] = useState<Set<Layer>>(new Set(['settlements', 'airfields', 'sites']));
  const [selected, setSelected] = useState<Selection | null>(null);
  const [asked, setAsked] = useState<'sent' | 'failed' | null>(null);
  const [recipes, setRecipes] = useState<RecipeView[] | null>(null);
  const [kinds, setKinds] = useState<string[] | null>(null);

  useEffect(() => {
    let live = true;
    setTerrain(null);
    setProfile(undefined);
    setNamed(null);
    setRelief(null);
    setAsked(null);
    fetchTerrains()
      .then((list) => {
        if (live)
          setTerrain(list.find((x) => x.worldName.toLowerCase() === world.toLowerCase()) ?? null);
      })
      .catch(() => undefined);
    fetchTerrainProfile(world)
      .then((p) => live && setProfile(p))
      .catch(() => live && setProfile(null));
    fetchNamed(world, hash)
      .then((n) => live && setNamed(n))
      .catch(() => live && setNamed({ modSetHash: null, places: [], airfields: [] }));
    fetchHeightmap(world)
      .then((m) => live && setRelief(m))
      .catch(() => live && setRelief(null));
    return () => {
      live = false;
    };
  }, [world, hash]);

  // What the terrain's ground lets the generator write, judged with the mod set that carries it.
  const modSet = named?.modSetHash ?? null;
  useEffect(() => {
    let live = true;
    setRecipes(null);
    setKinds(null);
    if (!modSet) return undefined;
    fetchRecipesFor(world, modSet)
      .then((list) => live && setRecipes(list))
      .catch(() => undefined);
    fetchTerrainKinds(modSet)
      .then((all) => live && setKinds(all[world.toLowerCase()] ?? null))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [world, modSet]);

  const features = profile?.features ?? null;
  const summary = profile?.summary ?? null;
  const points = useMemo(() => {
    const places = named?.places ?? [];
    const of = (list?: number[][]) =>
      (list ?? []).filter((p) => p.length >= 2).map((p) => ({ x: p[0], y: p[1], text: '' }));
    const out: Record<Layer, { x: number; y: number; text: string }[]> = {
      settlements: places
        .filter((p) => p.settlement)
        .map((p) => ({ x: p.x, y: p.y, text: p.name })),
      places: places.filter((p) => !p.settlement).map((p) => ({ x: p.x, y: p.y, text: p.name })),
      airfields: (named?.airfields ?? []).map((a) => ({
        x: a.x,
        y: a.y,
        text: t.airfieldLabel(a.kind, a.index),
      })),
      sites: (features?.sites ?? []).map((s) => ({
        x: s.x,
        y: s.y,
        text: t.siteLabel(s.count ?? 0),
      })),
      // The measured bridges when the survey has them: the rows of the table under the map.
      bridges: summary?.bridgeRecords.length
        ? summary.bridgeRecords.map((b) => ({ x: b.x, y: b.y, text: '' }))
        : of(features?.bridges),
      highGround: of(features?.highGround),
      flatAreas: of(features?.flatAreas),
      beaches: of(features?.beaches),
      lakes: of(features?.lakes),
    };
    return out;
  }, [named, features, summary, t]);

  const markers = useMemo(
    () =>
      LAYERS.filter((layer) => on.has(layer)).flatMap((layer) =>
        points[layer].slice(0, MAX_PINS).map((p, i) => ({
          id: `${layer}:${i}`,
          kind: 'icon' as const,
          symbol: LOOK[layer].symbol,
          colour: LOOK[layer].colour,
          position: [p.x, p.y],
          text: p.text || `${t.layers[layer]} · ${grid(p.x, p.y)}`,
        })),
      ),
    [on, points, t],
  );

  const worldSize = profile?.worldSize ?? terrain?.worldSize ?? relief?.worldSize ?? 0;
  const title = terrain?.displayName ?? world;
  const toggle = (layer: Layer) =>
    setOn((old) => {
      const next = new Set(old);
      if (next.has(layer)) next.delete(layer);
      else next.add(layer);
      return next;
    });
  /** A table row shows its pin: the layer goes on and the pin is the selected one, which labels it. */
  const show = (layer: Layer, index: number) => {
    setOn((old) => new Set(old).add(layer));
    setSelected({ kind: 'marker', id: `${layer}:${index}` });
  };

  const surveyed = profile?.status === 'DONE';
  // The last completed survey stays on the row while the next one runs or after it fails.
  const hasData = profile != null && (profile.cols != null || surveyed);
  const running =
    profile != null && ['QUEUED', 'STARTING', 'RUNNING'].includes(profile.status) && hasData;
  const version = profile?.surveyVersion ?? 1;
  const newest = profile?.currentSurveyVersion ?? version;
  const old = hasData && version < newest;
  const canSurvey = me.roles.includes('SERVER_MANAGER') || me.roles.includes('ADMIN');
  const resurvey = () => {
    surveyTerrain(world, hash ?? named?.modSetHash ?? null, true)
      .then((p) => {
        // The answer is the row as it stands now: the old data, and a survey on its way.
        setProfile((before) =>
          before ? { ...before, ...p, features: before.features, summary: before.summary } : before,
        );
        setAsked('sent');
      })
      .catch(() => setAsked('failed'));
  };

  /** What a layer's chip says after its name: the pins it has, and of how many when the survey kept only some. */
  const layerCount = (layer: Layer): string => {
    const shown = points[layer].length;
    const list = (SURVEY_LISTS as readonly string[]).includes(layer) ? summary?.lists[layer] : null;
    // The measured bridges are one per bridge, whatever the older capped list held.
    if (!list || (layer === 'bridges' && summary?.bridgeRecords.length)) return String(shown);
    if (list.total === null) return t.layerSample(shown);
    return list.capped ? t.layerCapped(shown, num(list.total)) : String(shown);
  };
  const seconds =
    profile?.startedAt && profile?.finishedAt
      ? Math.round((Date.parse(profile.finishedAt) - Date.parse(profile.startedAt)) / 1000)
      : null;
  const settlements = (named?.places ?? []).filter((p) => p.settlement);
  const others = (named?.places ?? []).filter((p) => !p.settlement);

  return (
    <Shell title={title} width="wide">
      <p className="muted small">
        <Link to="/catalog">{t.back}</Link> · <code>{world}</code>
        {worldSize > 0 && ` · ${(worldSize / 1000).toFixed(1)} km`}
        {terrain?.modName && ` · ${t.origin(terrain.modName)}`}
      </p>

      {profile === null && <p className="notice warn small">{t.notSurveyed}</p>}
      {running && (
        <p className="notice small" role="status">
          <i className="bi bi-arrow-repeat" aria-hidden="true" /> {t.surveyRunning}
        </p>
      )}
      {profile && !surveyed && !running && (
        <p className="notice small">
          {t.surveyState(ca.catalogPage2.terrainSurveyStates[profile.status] ?? profile.status)}
        </p>
      )}
      {old && (
        <p className="muted small old-survey">
          <i className="bi bi-clock-history" aria-hidden="true" /> {t.oldSurvey(version, newest)}
          {canSurvey && !running && asked !== 'sent' && (
            <>
              {' '}
              <button type="button" className="small ghost" onClick={resurvey}>
                <i className="bi bi-arrow-repeat" aria-hidden="true" />
                {ca.catalogPage2.terrainSurveyAgain}
              </button>
            </>
          )}
          {asked === 'sent' && ` ${t.resurveyAsked}`}
          {asked === 'failed' && ` ${t.resurveyFailed}`}
        </p>
      )}
      {named && !named.modSetHash && <p className="notice warn small">{t.notScanned}</p>}

      <TerrainSheet
        world={world}
        title={title}
        profile={profile ?? null}
        summary={summary}
        features={features}
        named={named}
        recipes={recipes}
        kinds={kinds}
        meta={
          hasData &&
          (profile?.finishedAt || profile?.surveyVersion != null) && (
            <p className="muted small">
              {[
                surveyed && profile?.finishedAt
                  ? t.surveyedOn(
                      new Date(profile.finishedAt).toLocaleString('ca', { dateStyle: 'medium' }),
                    )
                  : null,
                t.surveyVersion(version, newest),
                profile?.gridStep ? t.gridStep(profile.gridStep) : null,
                surveyed && seconds != null && seconds > 0
                  ? t.sheet.duration(t.sheet.seconds(seconds))
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )
        }
      />

      <VoiceRegionField world={world} canEdit={me.roles.includes('ADMIN')} />

      <div className="toolbar terrain-layers" role="group" aria-label={t.layersLabel}>
        {LAYERS.map((layer) => (
          <button
            key={layer}
            type="button"
            className={`chip toggle ${on.has(layer) ? 'on' : ''}`}
            aria-pressed={on.has(layer)}
            disabled={points[layer].length === 0}
            onClick={() => toggle(layer)}
          >
            {t.layers[layer]} · {layerCount(layer)}
          </button>
        ))}
      </div>
      {LAYERS.some(
        (layer) =>
          points[layer].length > MAX_PINS || layerCount(layer) !== String(points[layer].length),
      ) && <p className="muted small">{t.capped(MAX_PINS)}</p>}

      {worldSize > 0 ? (
        <MissionMap
          world={world}
          worldSize={worldSize}
          markers={markers}
          relief={relief}
          height={640}
          selected={selected}
          onSelect={setSelected}
        />
      ) : (
        <p className="muted">{t.noSize}</p>
      )}
      <p className="muted small">{t.mapHint}</p>

      {settlements.length + others.length > 0 && (
        <section aria-label={t.placesTitle}>
          <h2 className="section">
            {t.placesTitle} · {settlements.length + others.length}
          </h2>
          <ShortTable
            head={
              <>
                <tr>
                  <th>{t.colName}</th>
                  <th>{t.colType}</th>
                  <th>{t.colGrid}</th>
                  <th>{t.colRadius}</th>
                </tr>
              </>
            }
            rows={[
              ...settlements.map((p, i) => (
                <tr key={`s${i}`}>
                  <td>
                    <button
                      type="button"
                      className="linklike"
                      onClick={() => show('settlements', i)}
                    >
                      {p.name || '—'}
                    </button>
                  </td>
                  <td>{t.placeTypes[p.type] ?? p.type}</td>
                  <td className="num">{grid(p.x, p.y)}</td>
                  <td className="num">{p.radius > 0 ? `${p.radius} m` : '—'}</td>
                </tr>
              )),
              ...others.map((p, i) => (
                <tr key={`o${i}`}>
                  <td>
                    <button type="button" className="linklike" onClick={() => show('places', i)}>
                      {p.name || '—'}
                    </button>
                  </td>
                  <td>{t.placeTypes[p.type] ?? p.type}</td>
                  <td className="num">{grid(p.x, p.y)}</td>
                  <td className="num">{p.radius > 0 ? `${p.radius} m` : '—'}</td>
                </tr>
              )),
            ]}
          />
        </section>
      )}

      {(named?.airfields.length ?? 0) > 0 && (
        <section aria-label={t.layers.airfields}>
          <h2 className="section">
            {t.layers.airfields} · {named!.airfields.length}
          </h2>
          <ShortTable
            head={
              <>
                <tr>
                  <th>{t.colName}</th>
                  <th>{t.colGrid}</th>
                  <th>{t.colHeading}</th>
                </tr>
              </>
            }
            rows={[
              ...named!.airfields.map((a, i) => (
                <tr key={i}>
                  <td>
                    <button type="button" className="linklike" onClick={() => show('airfields', i)}>
                      {t.airfieldLabel(a.kind, a.index)}
                    </button>
                  </td>
                  <td className="num">{grid(a.x, a.y)}</td>
                  <td className="num">{a.heading}°</td>
                </tr>
              )),
            ]}
          />
        </section>
      )}

      {(summary?.runways.length ?? 0) > 0 && (
        <section aria-label={t.runwaysTitle}>
          <h2 className="section">
            {t.runwaysTitle} · {summary!.runways.length}
          </h2>
          <ShortTable
            head={
              <tr>
                <th>{t.colName}</th>
                <th>{t.colGrid}</th>
                <th>{t.colTakeoff}</th>
                <th>{t.colLength}</th>
                <th>{t.colSurface}</th>
              </tr>
            }
            rows={summary!.runways.map((r, i) => (
              <tr key={i}>
                <td>
                  {t.runwayNames[r.airport] ?? (r.name || '—')}
                  {r.airport === 'secondary' && r.index > 0 ? ` ${r.index}` : ''}
                </td>
                <td className="num">{grid(r.x, r.y)}</td>
                <td className="num">{r.heading}°</td>
                <td className="num">{r.length > 0 ? t.metres(num(r.length)) : '—'}</td>
                <td>{r.paved ? t.paved : t.unpaved}</td>
              </tr>
            ))}
          />
        </section>
      )}

      {(summary?.bridgeRecords.length ?? 0) > 0 && (
        <section aria-label={t.bridgesTitle}>
          <h2 className="section">
            {t.bridgesTitle} · {summary!.bridgeRecords.length}
          </h2>
          <ShortTable
            head={
              <tr>
                <th>{t.colGrid}</th>
                <th>{t.colSpan}</th>
                <th>{t.colOver}</th>
                <th>{t.colDrop}</th>
              </tr>
            }
            rows={summary!.bridgeRecords.map((b, i) => (
              <tr key={i}>
                <td className="num">
                  <button type="button" className="linklike" onClick={() => show('bridges', i)}>
                    {grid(b.x, b.y)}
                  </button>
                </td>
                <td className="num">{t.metres(num(b.span))}</td>
                <td>{b.overWater ? t.overWater : t.overLand}</td>
                <td className="num">{t.metres(num(b.drop))}</td>
              </tr>
            ))}
          />
        </section>
      )}

      {(summary?.largestMasses.length ?? 0) > 1 && (
        <section aria-label={t.massesTitle}>
          <h2 className="section">
            {t.massesTitle}
            {summary!.landMasses != null && ` · ${num(summary!.landMasses)}`}
          </h2>
          <ShortTable
            head={
              <tr>
                <th>{t.colGrid}</th>
                <th>{t.colArea}</th>
              </tr>
            }
            rows={summary!.largestMasses.map((m, i) => (
              <tr key={i}>
                <td className="num">{grid(m.x, m.y)}</td>
                <td className="num">{t.km2(num(m.km2))}</td>
              </tr>
            ))}
          />
        </section>
      )}

      {(features?.sites?.length ?? 0) > 0 && (
        <section aria-label={t.layers.sites}>
          <h2 className="section">
            {t.layers.sites} · {features!.sites!.length}
          </h2>
          <p className="muted small">{t.sitesHint}</p>
          <ShortTable
            head={
              <>
                <tr>
                  <th>{t.colGrid}</th>
                  <th>{t.colObjects}</th>
                  <th>{t.colRadius}</th>
                  <th>{t.colKinds}</th>
                </tr>
              </>
            }
            rows={[
              ...features!.sites!.map((s, i) => (
                <tr key={i}>
                  <td className="num">
                    <button type="button" className="linklike" onClick={() => show('sites', i)}>
                      {grid(s.x, s.y)}
                    </button>
                  </td>
                  <td className="num">{s.count ?? '—'}</td>
                  <td className="num">{s.radius ? `${s.radius} m` : '—'}</td>
                  <td className="muted small">
                    {Object.entries(s.kinds ?? {})
                      .sort((a, b) => b[1] - a[1])
                      .map(([kind, n]) => `${t.siteKinds[kind] ?? kind} ${n}`)
                      .join(' · ')}
                  </td>
                </tr>
              )),
            ]}
          />
        </section>
      )}
    </Shell>
  );
}

/** How many rows a terrain's table shows before "all of them". */
const SHORT_ROWS = 12;

/**
 * A table that shows its first rows and a button for the rest, instead of a box
 * 420 px tall with a scroll bar of its own inside a page that scrolls (the
 * owner, 2026-09-28: no content boxed in a scroll while the page has room).
 */
function ShortTable({ head, rows }: { head: ReactNode; rows: ReactNode[] }) {
  const t = ca.terrainPage;
  const [all, setAll] = useState(false);
  return (
    <>
      <div className="table-wrap">
        <table className="data">
          <thead>{head}</thead>
          <tbody>{all ? rows : rows.slice(0, SHORT_ROWS)}</tbody>
        </table>
      </div>
      {rows.length > SHORT_ROWS && (
        <button type="button" className="small ghost show-more" onClick={() => setAll(!all)}>
          <i className={`bi bi-chevron-${all ? 'up' : 'down'}`} aria-hidden="true" />
          {all ? t.showFewer : t.showAll(rows.length)}
        </button>
      )}
    </>
  );
}
